// tslint:disable:object-literal-sort-keys

/**
 * @file
 * Processing required for BasstrakkLeaderboard
 */

import * as React from 'react';

// Config

// Components
import CutlineText from '../../components/CutlineText/CutlineText';

// Types
import { AnglerId, CutlineRow, IBasstrakkCatch } from '../../types';
import { ICatchesSummary, ISponsors } from './types';

/**
 * Provided an array of catches and an AnglerID, returns an object with helpful
 * information about the angler's catches so far
 */
export function basstrakkCatchesTop5(
  anglerId: AnglerId,
  catches: IBasstrakkCatch[] = []
): ICatchesSummary {
  // Only catches for this angler TODAY
  const anglerCatches =
    catches.filter(fishCatch => anglerId === fishCatch.AnglerId) || [];

  // Catches actually kept
  const kept = anglerCatches.filter(({ IsKept }) => IsKept === 'Y');
  // Catches culled
  const culled = anglerCatches.filter(({ IsKept }) => IsKept === 'N');

  // Top 5 catches by weight, sorted by weight
  const keptTop5 = kept
    .sort(
      (fishCatchA, fishCatchB) =>
        fishCatchB.FishWeightOz - fishCatchA.FishWeightOz
    )
    .slice(0, 5);
  // Top 5 total weight
  const keptTop5TotalWeight = keptTop5.reduce(
    (sum, { FishWeightOz }) => sum + FishWeightOz,
    0
  );
  // Top 5 by weight, sorted by time (slice() copies so we don't mutate original)
  const keptTop5TimeSort = keptTop5
    .slice()
    .sort(
      (fishCatchA, fishCatchB) =>
        new Date(fishCatchA.Time).getTime() -
        new Date(fishCatchB.Time).getTime()
    );

  return {
    keptTop5,
    keptTop5TotalWeight,
    keptTop5TimeSort,
    keptCount: kept.length,
    culledCount: culled.length,
  };
}

/**
 * Given an angler id and a list of sponsors, returns an array of sponsor urls
 * for a given angler id.
 */
export const getSponsorsByAnglerId = (
  anglerId: AnglerId,
  sponsors: ISponsors
) =>
  // Get logo urls for each sponsor associated with the angler.
  sponsors &&
  Object.keys(sponsors)
    // Filter sponsors by anglerId and only those with valid imageUrls.
    .filter(
      sponsorId =>
        sponsors[parseInt(sponsorId, 10)].anglers.includes(anglerId) &&
        sponsors[parseInt(sponsorId, 10)].imageUrl
    )
    // Map over sponsorIds and look up the imageUrls.
    .map(sponsorId => {
      return {
        imageUrl: sponsors[parseInt(sponsorId, 10)].imageUrl,
        tagline: sponsors[parseInt(sponsorId, 10)].tagline
          ? sponsors[parseInt(sponsorId, 10)].tagline
          : '',
        bgColor: sponsors[parseInt(sponsorId, 10)].bgColor
          ? sponsors[parseInt(sponsorId, 10)].bgColor
          : '',
        textColor: sponsors[parseInt(sponsorId, 10)].textColor
          ? sponsors[parseInt(sponsorId, 10)].textColor
          : '',
      };
    })
    // Keep sponsors in order.
    .reverse();

/**
 * Creates the cutline row for BasstrakkLeaderboard.
 * @param cutlineText
 * @param key
 */
export const basstrakkCutlineRow: CutlineRow = (
  cutlineText: string,
  key: string
) => (
  <tr key={key}>
    <td colSpan={8}>
      <table>
        <tbody>
          <tr className="cutline" data-testid="cutline-row">
            <td>
              <CutlineText text={cutlineText} />
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
);
